import { Button } from "@legal-500/legal500-design-system";
import { useCallback, useState } from "react";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import * as api from "../../../api/client.js";
import ResearchJobButtons from "./ResearchJobButtons.js";
import { ResearcherAllocationsImport } from "./ResearcherAllocationsImport.js";
import styles from "./ResearchDataImport.module.css";

type SeedBpaTaxonomyMappingResponse = {
  inserted: number;
  updated: number;
  skipped: number;
  errors: number;
  logs: string[];
  requestId: string;
};

function ResearchDataImportPage() {
  const { hasPermission } = usePermissions();
  const [bpaImporting, setBpaImporting] = useState(false);
  const [bpaResult, setBpaResult] = useState<SeedBpaTaxonomyMappingResponse | null>(null);
  const [bpaError, setBpaError] = useState<string | null>(null);

  const handleBpaImport = useCallback(async () => {
    setBpaImporting(true);
    setBpaResult(null);
    setBpaError(null);
    try {
      const res = await api.post<SeedBpaTaxonomyMappingResponse>(
        "/v1/research/seed/bpa-taxonomy-mapping",
        {},
      );
      setBpaResult(res);
    } catch (err) {
      setBpaError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setBpaImporting(false);
    }
  }, []);

  if (!hasPermission("staff.research.admin")) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Research Data Import"
        subtitle="Manual one-off imports for the research schema"
      />

      <div className={styles.content}>
        <div className={styles.resultCard}>
          <p className={styles.sectionDescription}>
            Research data — publication config, ranking entities, BPAs, ranking tables, edition BPA
            links, editorial content and legacy rankings — is imported by two wp-syncer research
            jobs, not by the main sync. The nightly job imports current editions for each
            publication whose <code>research-daily-&lt;slug&gt;</code> flag is on, and EMEA's steps
            whose <code>sync-emea-&lt;step&gt;</code> flags are on. The old-editions job imports
            archived editions and is started by hand.
          </p>
        </div>

        <ResearchJobButtons />

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>BPA Taxonomy Mapping</h2>
          <p className={styles.sectionDescription}>
            Seed research_bpa &rarr; taxonomy.bpa mappings from the CSV mapping file
          </p>
          <div className={styles.formAction}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleBpaImport}
              disabled={bpaImporting}
            >
              {bpaImporting ? "Importing..." : "Import Mappings"}
            </Button>
          </div>

          {bpaError && <div className={`${styles.notification} ${styles.error}`}>{bpaError}</div>}

          {bpaResult && (
            <div className={styles.resultCard}>
              <h3>Import Complete</h3>
              <dl className={styles.resultGrid}>
                <dt>Inserted</dt>
                <dd>{bpaResult.inserted}</dd>
                <dt>Updated</dt>
                <dd>{bpaResult.updated}</dd>
                <dt>Skipped</dt>
                <dd>{bpaResult.skipped}</dd>
                <dt>Errors</dt>
                <dd>{bpaResult.errors}</dd>
              </dl>
              {bpaResult.logs.length > 0 && (
                <details className={styles.logsDetails}>
                  <summary>Logs ({bpaResult.logs.length} entries)</summary>
                  <pre className={styles.logContent}>{bpaResult.logs.join("\n")}</pre>
                </details>
              )}
            </div>
          )}
        </section>

        <ResearcherAllocationsImport />
      </div>
    </div>
  );
}

export default ResearchDataImportPage;
